/**
 * Pure helper functions for the ChangeAttribute plugin
 * @module
 */

var _ = require('optimizely-client-fn');
var enums = require('core/action/enums');

/**
 * Move "hide" and "remove" attributes to change.css, since they are in effect
 * changes to the element's style. This step has to happen so long as there are
 * pseudo-attributes like `hide` and `remove` that reduce to style changes.
 * All style-altering changes that are not Inline CSS must be stored in the `css`
 * object prior to change application to guarantee that Inline CSS takes
 * highest precedence. See CJS-637.
 *
 * @param change {Change}
 * @example change.attribute === { hide: true } => change.css === { visibility: 'hidden' }
 */
exports.transformVisibilityAttributesToCSS = function (change:Change) {
  if (!change.attributes) {
    return change;
  }

  if (change.attributes[enums.selectorChangeType.HIDE] || change.attributes[enums.selectorChangeType.REMOVE]) {
    var changeAfterTransform = _.extend({ css: {} }, _.cloneDeep(change));

    if (change.attributes[enums.selectorChangeType.HIDE]) {
      changeAfterTransform.css.visibility = 'hidden';
      delete changeAfterTransform.attributes[enums.selectorChangeType.HIDE];
    }

    if (change.attributes[enums.selectorChangeType.REMOVE]) {
      changeAfterTransform.css.display = 'none';
      delete changeAfterTransform.attributes[enums.selectorChangeType.REMOVE];
    }

    return changeAfterTransform;
  }

  return change;
};

/**
 * Combine CSS changes with inline style changes to avoid having separate undo actions since
 * both are making inline style changes.
 *
 * Editor inline style order of operations
 * - https://confluence.sso.episerver.net/display/EXPENG/Editor+Inline+Style+-+Order+of+Operations
 *
 * @param {string} originalInlineStyles Page element style of the format of cssText from https://www.w3.org/TR/DOM-Level-2-Style/css.html#CSS-CSSStyleDeclaration
 * @param {Object} change Optimizely change object
 * @returns {string|undefined} Computed inline styles if css and style changes were made, otherwise undefined
 *
 * @example
 *  originalInlineStyles === "text-align: right;"
 *  change === {
 *    "css": {
 *      "border-style": "solid",
 *      "border-width": "10px"
 *    },
 *    "attributes": {
 *      "style": "text-decoration: underline;"
 *    }
 *  }
 *  => "border-style: solid; border-width: 10px; text-decoration: underline;"
 */
exports.createStylesFromChange = function (originalInlineStyles:string, change:any): string|undefined {
  if (_.isEmpty(change.css)) {
    // Return Inline CSS stored in change.attributes.style, even if it's undefined
    return change.attributes.style;
  }

  var computedStylesFromCssChanges = '';
  var updatedInlineStyles = change.attributes.style || ''; // Ensure at least an empty string is returned

  _.each(change.css, function (value, property) {
    var pattern = new RegExp(property + '\\s?:');

    // Ignore CSS properties which are defined in inline style
    if (pattern.test(updatedInlineStyles)) {
      return;
    }

    computedStylesFromCssChanges += property + ':' + value + ';';
  });

  // When change.attributes.style is undefined, return the element's original inline styles followed by the computed CSS changes
  // Style declarations in computed CSS changes will trump duplicate ones in the original inline styles since they are concatenated after
  if (_.isUndefined(change.attributes.style)) {
    return (originalInlineStyles || '') + computedStylesFromCssChanges;
  }

  // Otherwise, our changes attributes.style trumps the existing inline style
  // Prefix any specific CSS changes to ensure attributes.style has more specificity
  return computedStylesFromCssChanges + updatedInlineStyles;
};
